import { reactive, ref, watch } from "vue";
import api from "@/utils/axios";

interface ServerTableOptions {
  /** API url of the list. */
  url: string | (() => string);
  /** Extra query params (note id, ref…), read on every load. */
  params?: () => Record<string, any>;
  /** Picks the Laravel paginator out of the response (default: `res.data`). */
  pick?: (data: any) => any;
  perPage?: number;
  /** Delay before a typed search is sent to the API. */
  debounce?: number;
}

// Server-side pagination + search for a table: only one page of rows is ever fetched.
// `pager` has the shape of a Laravel paginator so it plugs into <PaginatorContent>
// (`@paginate="load"`), `perPage` into the "Voir" select, `searchTerm` into the search input.
export function useServerTable<T = any>(options: ServerTableOptions) {
  const rows = ref<T[]>([]);
  const loading = ref(false);
  const searchTerm = ref("");
  const perPage = ref(options.perPage ?? 10);
  const pager = reactive({
    current_page: 1,
    last_page: 1,
    per_page: perPage.value,
    total: 0,
    from: 0,
    to: 0,
  });

  let requestId = 0;

  async function load(page: number | Event = 1) {
    const current = typeof page === "number" ? page : 1;
    const id = ++requestId;
    loading.value = true;
    try {
      const url = typeof options.url === "function" ? options.url() : options.url;
      const res = await api.get(url, {
        params: {
          ...(options.params ? options.params() : {}),
          searchTerm: searchTerm.value.trim() || undefined,
          page: current,
          per_page: perPage.value,
        },
      });
      if (id !== requestId) return; // a newer request (search typed meanwhile) wins
      const paginator = (options.pick ? options.pick(res.data) : res.data) ?? {};
      rows.value = paginator.data ?? [];
      pager.current_page = paginator.current_page ?? 1;
      pager.last_page = paginator.last_page ?? 1;
      pager.per_page = paginator.per_page ?? perPage.value;
      pager.total = paginator.total ?? rows.value.length;
      pager.from = paginator.from ?? 0;
      pager.to = paginator.to ?? 0;
    } finally {
      if (id === requestId) loading.value = false;
    }
  }

  let timer: number | undefined;
  watch(searchTerm, () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => load(1), options.debounce ?? 400);
  });
  // vue-select 4 does not emit "input": react to the v-model instead
  watch(perPage, () => load(1));

  return { rows, pager, loading, searchTerm, perPage, load };
}
